import { CloseOutlineIcon, InfoIcon } from '@carboncopyco/icons';
import type { IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import React from 'react';
import type { ToastOptions } from 'react-toastify';
import { Spacer } from '../Spacer';
import { TextButton, TextCaption } from '../Typography';
import {
  bgColor,
  borderColor,
  closeIconColor,
  infoIconColor,
  textColor,
} from './colors';

export type NotificationType =
  | 'dark'
  | 'error'
  | 'warning'
  | 'info'
  | 'success';

export interface NotificationSnackbarProps {
  type?: NotificationType;
  label: string;
  actionLabel?: string;
  cb?: () => void;
  iconName?: React.ReactElement<IconProps>;
  hideClose?: boolean;
  linkLabel?: string | React.ReactNode;
  linkCB?: () => void;
}

export const snackbarDefaultParams: Pick<
  ToastOptions,
  | 'autoClose'
  | 'position'
  | 'closeButton'
  | 'icon'
  | 'pauseOnFocusLoss'
  | 'className'
> = {
  autoClose: 6000,
  position: 'top-right',
  closeButton: false,
  icon: false,
  pauseOnFocusLoss: false,
  className: () =>
    'leading-none mb-2.5 justify-between overflow-hidden cursor-pointer rounded-md shadow-ds-sm',
};

export const NotificationSnackbar = ({
  type = 'dark',
  label,
  iconName,
  cb,
  linkCB,
  linkLabel,
  actionLabel,
  hideClose,
}: NotificationSnackbarProps) => (
  <div className="min-h-10">
    {actionLabel ? (
      <div className="flex items-center bg-transparent box-border w-max h-full">
        <div
          className={classNames(
            'flex justify-center shadow-ds-lg  items-center border box-border rounded-l-md rounded-r-none cursor-default pointer-events-none p-3 px-4',
            [bgColor[type], borderColor[type]]
          )}
        >
          {iconName && (
            <>
              <div>
                {React.cloneElement(iconName, {
                  color: infoIconColor[type],
                })}
              </div>
              <Spacer amount={0.5} />
            </>
          )}

          <TextCaption
            label={label}
            color={textColor[type]}
          />
        </div>
        <Spacer amount={0.1} />
        <div
          onClick={cb}
          className={classNames([
            'flex justify-center items-center shadow-ds-lg border box-border rounded-r-md rounded-l-none cursor-pointer pointer-events-auto py-2.5 px-4',
            bgColor[type],
            borderColor[type],
          ])}
        >
          <TextButton
            label={actionLabel}
            color={textColor[type]}
          />
        </div>
      </div>
    ) : !hideClose ? (
      <div
        className={classNames([
          'flex justify-between items-center rounded-md h-full py-2.5 px-4',
          bgColor[type],
        ])}
      >
        <div className="flex items-center">
          <div>
            {React.cloneElement(iconName ?? <InfoIcon />, {
              color: infoIconColor[type],
            })}
          </div>
          <Spacer amount={0.5} />
          <div className="flex flex-wrap items-center">
            <div className="flex items-center">
              <TextCaption
                label={label}
                color={textColor[type]}
              />
            </div>
            {linkLabel && typeof linkLabel === 'string' ? (
              <div
                className="flex items-center ml-1"
                onClick={linkCB}
              >
                <TextButton
                  label={linkLabel}
                  color={textColor[type]}
                />
              </div>
            ) : (
              linkLabel && <div onClick={linkCB}>{linkLabel}</div>
            )}
          </div>
        </div>
        <Spacer amount={0.5} />
        <div>
          <CloseOutlineIcon
            color={closeIconColor[type]}
            size={16}
          />
        </div>
      </div>
    ) : (
      <div
        className={classNames([
          'flex justify-center items-center rounded-md py-3 px-4 shadow-ds-lg h-full',
          bgColor[type],
        ])}
      >
        <TextCaption
          label={label}
          color={textColor[type]}
        />
      </div>
    )}
  </div>
);
